import { useEffect, useState } from "react";
import { $crud } from "../../utils/CrudFactory";
import dayjs from "dayjs";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { orderStatusIcon } from "../../pages/orders/OrderDetail";
import tick from "../../assets/images/tickNew.png";
import truck from "../../assets/images/svg/truck.svg";
import { usePagination } from "../usePagination";

const steps = [];

export default function useOrderDetail() {
  const [mergedSteps, setMergedSteps] = useState([]);
  const [client, setClient] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const [orderItems, setOrderItems] = useState([]);
  const [filtrationData, setFiltrationData] = useState({
    search: "",
  });
  const [modalState, setModalState] = useState({
    invoiceOpen: false,
    podOpen: false,
    tipsOpen: false,
  });
  const [order, setOrder] = useState(
    location.state?.selectedOrder ||
      JSON.parse(localStorage.getItem("selectedOrder"))
  );

  useEffect(() => {
    setOrder(location.state?.selectedOrder);
  }, [location.state]);

  useEffect(() => {
    // Save order to local storage whenever it updates
    localStorage.setItem("selectedOrder", JSON.stringify(order));
  }, [order]);

  useEffect(() => {
    // Combine order.positionHistory with existing steps
    const merged = [...steps];
    if (order && order.orderHistory) {
      order.orderHistory.forEach((historyItem, index, orderHistory) => {
        const diffMs =
          new Date(historyItem.timestamp).getTime() -
          new Date(orderHistory[index - 1]?.timestamp).getTime();
        const duration = moment.duration(diffMs);

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        const display =
          index === 0
            ? ""
            : `${
                days < 1 ? "" : days < 2 ? `${days} day` : `${days} days`
              } ${hours} hr${hours !== 1 ? "s" : ""} ${minutes} min${
                minutes !== 1 ? "s" : ""
              }`;

        let label = historyItem.status;
        let desc = `${dayjs(historyItem.timestamp).format(
          "ddd,DD MMM YYYY, h:mm A"
        )} ${display}`;
        const iconimage = Object.keys(orderStatusIcon).find((val) =>
          historyItem.status.includes(val)
        );
        let icon = (
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            {!/Working/i.test(historyItem.status) && (
              <img src={tick} alt="tick" style={{}} />
            )}
            <img src={orderStatusIcon[iconimage] || truck} alt="orderstatus" />
          </div>
        );

        merged.push({
          label,
          description: desc,
          icon,
          reason: historyItem.reason,
          tooltip: true,
          timestamp: historyItem.timestamp,
        });
      });
    }

    const sorted = merged.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
    setMergedSteps(sorted);
  }, [order, steps]);

  async function retrieveOrderItems() {
    const isFilterApplied = Object.values(filtrationData).some(
      (value) => value !== "" && value !== false
    );

    const params = {
      search: filtrationData.search,
    };

    // Conditionally include limit and page if no filters are applied
    if (!isFilterApplied) {
      params.limit = limit;
      params.page = page;
    }
    const { data } = await $crud.retrieve("admin/order-items", {
      orderId: order.orderId,
      search: filtrationData.search,
    });
    return data;
  }

  async function retrieveClientDetails() {
    const { data } = await $crud.retrieve("admin/client", {
      customerID: order.customerId,
    });
    return data;
  }

  useEffect(() => {
    const id = setTimeout(
      () => {
        retrieveClientDetails().then((res) => {
          setClient(res);
        });
        retrieveOrderItems().then((res) => {
          const dropoff = res?.orderItems?.filter((a) => a.type !== "Pickup");
          const pickups = res?.orderItems
            ?.filter((obj) => obj.type === "Pickup")
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          setOrderItems([...pickups, ...dropoff]);
        });
      },
      searchInput.length > 0 ? 300 : 0
    );

    return () => clearTimeout(id);
  }, [order, filtrationData]);

  const [activePosition, setActivePosition] = useState(order.position.status);
  const [newPosition, setNewPosition] = useState(""); // state to open position modal
  const [positionModalOpen, setPositionModalOpen] = useState(false); // state to open position modal
  const [positionPlaceholder, setPositionPlaceholder] = useState(""); // state to open position modal
  const [positionBtn, setPositionBtn] = useState(""); // state to open position modal
  const [positionModalTitle, setPositionModalTitle] = useState(""); //state to display position modal title
  const [selectedRow, setSelectedRow] = useState(null); // To store the selected row for which position is being updated
  const { page, limit, setLimit, setPage, total, setTotal } = usePagination(); // PAGINATION PROPS
  const [searchInput, setSearchInput] = useState("");

  // function to close position modal---------------------------------
  const handleCloseModal = () => {
    setPositionModalTitle(false);
    setSelectedRow(null);
    setPositionModalOpen("");
  };
  // function to call the api and update the comment -------------------------------------
  const handlePositionSubmit = async (reason) => {
    // Logic to update position with reason
    const { data, type } = await $crud.put(`admin/order`, {
      position: newPosition,
      comment: reason,
      _id: selectedRow._id,
    });
    // console.log(data);
    if (type === "success") {
      const updatedOrder = data?.order;
      if (Array.isArray(updatedOrder.position)) {
        updatedOrder.position = updatedOrder.position[0];
      }
      

      // Update state with normalized data
      setOrder(updatedOrder);
      setActivePosition(updatedOrder.position.status);
      handleCloseModal();
    }
    // handleCloseModal();
  };

  useEffect(() => {
    setActivePosition(order.position.status);
  }, [order]);

  const getStatusStyles = (status) => {
    switch (status) {
      case "Booked":
        return {
          background: "#1ACA6D",
          color: "#ffffff",
        };
      case "Processing":
        return {
          background: "#FB970B",
          color: "#ffffff",
        };
      case "Delivered":
        return {
          background: "#074F24",
          color: "#ffffff",
        };
      default:
        return {
          background: "#FFF0ED",
          color: "rgba(254, 133, 106, 1)",
        };
    }
  };
  const styles = getStatusStyles(order.currentStatus.status);

  return {
    order,
    orderItems,
    navigate,
    setSearchInput,
    setFiltrationData,
    styles,
    selectedRow,
    handleCloseModal,
    handlePositionSubmit,
    positionBtn,
    positionPlaceholder,
    client,
    mergedSteps,
    filtrationData,
    limit,
    page,
    setLimit,
    setPage,
    total,
    positionModalOpen,
    positionModalTitle,
    searchInput,
    modalState,
    setModalState,
    setOrderItems,
  };
}
